import React from 'react';
import { motion } from 'framer-motion';
import { Cloud, Database, Server, Monitor, Boxes, Activity } from 'lucide-react';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';

const serviceIcons = {
  'Amazon SageMaker': Database,
  'Amazon Bedrock': Cloud,
  'Amazon EC2 (GPU)': Server,
  'Amazon S3': Database,
  'Amazon EKS': Boxes,
  'Amazon CloudWatch': Activity,
};

const priorityColors = {
  Critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  High: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  Medium: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

const AWSSlide = ({ data }) => {
  return (
    <div className="w-full max-w-6xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <div className="flex items-center justify-center gap-3 mb-4">
          <Cloud className="w-10 h-10 text-orange-400" />
          <h2 className="text-4xl md:text-5xl font-bold text-white">{data.title}</h2>
        </div>
        <p className="text-xl text-slate-400">{data.subtitle}</p>
      </motion.div>

      {/* Services Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
        {data.services.map((service, index) => {
          const Icon = serviceIcons[service.service] || Cloud;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
            >
              <Card className="bg-slate-800/50 border-slate-700/50 p-4 h-full">
                <div className="flex items-start justify-between mb-2">
                  <Icon className="w-5 h-5 text-orange-400" />
                  <Badge className={`text-xs ${priorityColors[service.priority]}`}>
                    {service.priority}
                  </Badge>
                </div>
                <h4 className="text-white font-semibold text-sm mb-1">{service.service}</h4>
                <p className="text-slate-400 text-xs">{service.usage}</p>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Credits Usage */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Card className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border-orange-500/20 p-5">
          <h3 className="text-lg font-semibold text-white mb-4">Projected Credits Usage</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">Model Training</span>
                <span className="text-orange-400">{data.creditsUsage.training}</span>
              </div>
              <Progress value={40} className="h-2 bg-slate-700" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">Inference</span>
                <span className="text-orange-400">{data.creditsUsage.inference}</span>
              </div>
              <Progress value={30} className="h-2 bg-slate-700" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">Storage</span>
                <span className="text-orange-400">{data.creditsUsage.storage}</span>
              </div>
              <Progress value={15} className="h-2 bg-slate-700" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">Other Services</span>
                <span className="text-orange-400">{data.creditsUsage.other}</span>
              </div>
              <Progress value={15} className="h-2 bg-slate-700" />
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default AWSSlide;
